import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { forwardRef, useState, useEffect, useId } from 'react';
import { createChainedFunction } from '@atc/create-chained-function';
import clsx from 'clsx';
import ErrorMessages from '../ErrorMessages/index.js';
import FormElementLabel from '../FormElementLabel/index.js';
import FormElementHelp from '../FormElementHelp/index.js';
import FormElementFeedback from '../FormElementFeedback/index.js';
const OPTIONAL = '(optional)';

/**
 *  Describe the component
 *
 */
const Input = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    className,
    component = 'input',
    errors = [],
    help,
    hasFeedback = false,
    label,
    labelProps = {},
    layout = 'vertical',
    onFeedbackClick,
    showErrors,
    showErrorMessage = true,
    type = 'text',
    formClassName,
    onBlur,
    value = '',
    disabled = false,
    ...rest
  } = _ref;
  const isInsideLayout = layout === 'inside';
  const isHorizontalLayout = layout === 'horizontal';
  const [hasValue, setHasValue] = useState(!!value);
  useEffect(() => {
    setHasValue(!!value);
  }, [value]);

  // use react18 useId to generate unique id
  const id = useId();
  const inputClassName = clsx('form-control', {
    'has-value': hasValue
  }, className);
  const handleOnFocus = event => {
    if (isInsideLayout) {
      rest.placeholder = '';
    }
    event.target.select();
  };
  const handleOnBlur = event => {
    setHasValue(!!event?.target?.value);
  };
  if (isInsideLayout) {
    rest.placeholder = label || labelProps.label;
    if (labelProps.optional) {
      rest.placeholder = `${rest.placeholder} ${OPTIONAL}`;
    }
  }
  const hasError = showErrors && showErrorMessage;
  if (hasError) {
    rest['aria-describedby'] = `form-error-${rest.id}`;
  }
  const elementOnly = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(component || 'input', {
    // add || 'input' to avoid storybook error
    id,
    key: id,
    className: inputClassName,
    type,
    // Only pass ref to what can accept it:
    ...(typeof component === 'string' // DOM elements ('input', 'div', etc.)
    || component && typeof component.$$typeof === 'symbol' // Components wrapped in forwardRef
    && component.$$typeof.toString() === 'Symbol(react.forward_ref)' ? {
      ref
    } : {}),
    onFocus: handleOnFocus,
    onBlur: createChainedFunction(handleOnBlur, onBlur),
    value,
    disabled,
    ...rest
  }), /*#__PURE__*/_jsx(FormElementFeedback, {
    show: hasFeedback && showErrors || typeof onFeedbackClick === 'function' && !!rest.value,
    onFeedbackClick: onFeedbackClick
  }), /*#__PURE__*/_jsx(FormElementHelp, {
    help: help
  }), /*#__PURE__*/_jsx(ErrorMessages, {
    id: `form-error-${rest.id}`,
    errors: errors,
    showErrors: hasError
  }));
  const formGroupClasses = clsx('form-group', formClassName, {
    'has-feedback': hasFeedback && showErrors,
    'has-error': hasError,
    'row display-flex align-items-baseline': isHorizontalLayout,
    'form-group-inside': isInsideLayout,
    'has-value': hasValue,
    disabled
  });
  if (layout === 'elementOnly') {
    return elementOnly;
  }
  const elementHorizontal = /*#__PURE__*/_jsx("div", {
    className: "col-xs-5"
  }, void 0, elementOnly);
  return /*#__PURE__*/_jsx("div", {
    "data-cmp": "input",
    className: formGroupClasses
  }, void 0, /*#__PURE__*/_jsx(React.Fragment, {}, "input-label", /*#__PURE__*/React.createElement(FormElementLabel, Object.assign({
    label: label
  }, labelProps, {
    htmlFor: labelProps.htmlFor || rest.id || id,
    className: labelProps.className || 'control-label',
    layout: layout
  }))), /*#__PURE__*/_jsx(React.Fragment, {}, "input-element", isHorizontalLayout ? elementHorizontal : elementOnly));
});
export default Input;